import { PriceDisplayType, Prices } from "../types/pricing/price-display.types";
import { EstimatedPrice, ProductPriceDetails } from "../types/pricing/product-price-details.types";

export const calculateEvaluatedQuantity = (
  priceDetails: ProductPriceDetails | undefined,
  currentQuantity: string | undefined,
  lastValidQuantity: string | undefined,
): string => {
  const { quantityMetaData, prices } = priceDetails ?? {};
  const { minimumOrderableQuantity, defaultQuantity } = quantityMetaData ?? {};
  const estimatedPrices = prices?.estimatedPrices ?? {};

  const fallbackQuantity =
    defaultQuantity ?? minimumOrderableQuantity ?? Number(Object.keys(estimatedPrices)[0] ?? 0);

  const hasValidPriceData = prices?.estimatedPrices;

  if (priceDetails?.displayStrategy === PriceDisplayType.UnitRangePrice) {
    return (minimumOrderableQuantity ?? fallbackQuantity).toString();
  }
  if (
    hasValidPriceData &&
    currentQuantity &&
    Number(currentQuantity) > 0 &&
    estimatedPrices[Number(currentQuantity)]
  ) {
    return currentQuantity;
  }
  if (lastValidQuantity && Number(lastValidQuantity) > 0 && estimatedPrices[Number(lastValidQuantity)]) {
    return lastValidQuantity;
  }
  return fallbackQuantity.toString();
};

export const calculateResolvedPrices = (
  priceDetails: ProductPriceDetails | undefined,
  evaluatedQuantity: string,
): Prices | null => {
  if (!priceDetails || isNaN(Number(evaluatedQuantity))) return null;

  const { displayStrategy, prices, quantityMetaData } = priceDetails;
  const { estimatedPrices } = prices;
  const { minimumOrderableQuantity, maximumOrderableQuantity } = quantityMetaData ?? {};
  const isUnitRange = displayStrategy === PriceDisplayType.UnitRangePrice;
  const isDifferentialUnitRangePrice = displayStrategy === PriceDisplayType.DifferentialUnitRangePrice;
  const isDifferentialPrice = displayStrategy === PriceDisplayType.DifferentialPrice;
  const isDifferentialUnitPrice = displayStrategy === PriceDisplayType.UnitDifferentialPrice;

  // Handle Unit Range display strategy separately
  if (minimumOrderableQuantity && maximumOrderableQuantity) {
    const priceAtMin = estimatedPrices[minimumOrderableQuantity];
    const priceAtMax = estimatedPrices[maximumOrderableQuantity];
    if (isUnitRange) {
      return getUnitRangePrices(priceAtMin, priceAtMax);
    }
    if (isDifferentialUnitRangePrice) {
      return getDifferentialRangePrices(priceAtMin, priceAtMax);
    }
  }
  const currentEstimatedPrice = estimatedPrices[Number(evaluatedQuantity)];

  if (isDifferentialPrice || isDifferentialUnitPrice) {
    return getDifferentialPrices(currentEstimatedPrice);
  }

  return getStandardPrices(currentEstimatedPrice);
};

const getDifferentialRangePrices = (minPrice: EstimatedPrice, maxPrice: EstimatedPrice) => ({
  priceDifferenceAtMaximumPrice: maxPrice?.alternativeOptionPrices?.differentialUnitListPrice,
  discountedPriceDifferenceAtMaximumPrice: maxPrice?.alternativeOptionPrices?.differentialUnitDiscountedPrice,
  priceDifferenceAtMinimumPrice: minPrice?.alternativeOptionPrices?.differentialUnitListPrice,
  discountedPriceDifferenceAtMinimumPrice: minPrice?.alternativeOptionPrices?.differentialUnitDiscountedPrice,
});

const getUnitRangePrices = (minPrice: EstimatedPrice, maxPrice: EstimatedPrice) => ({
  highestListPrice: minPrice?.unitListPrice,
  highestDiscountedPrice: minPrice?.unitDiscountedPrice,
  lowestListPrice: maxPrice?.unitListPrice,
  lowestDiscountedPrice: maxPrice?.unitDiscountedPrice,
  setupFeeListPrice: minPrice?.setupFeeListPrice,
  setupFeeDiscountedPrice: minPrice?.setupFeeDiscountedPrice,
});

const getDifferentialPrices = (current: EstimatedPrice) => {
  return {
    differentialListPrice: current?.alternativeOptionPrices?.differentialListPrice,
    differentialDiscountedPrice: current?.alternativeOptionPrices?.differentialDiscountedPrice,
    differentialUnitListPrice: current?.alternativeOptionPrices?.differentialUnitListPrice,
    differentialUnitDiscountedPrice: current?.alternativeOptionPrices?.differentialUnitDiscountedPrice,
  };
};

const getStandardPrices = (current: EstimatedPrice) => {
  return {
    totalListPrice: current.totalListPrice,
    totalDiscountedPrice: current.totalDiscountedPrice,
    unitListPrice: current.unitListPrice,
    unitDiscountedPrice: current.unitDiscountedPrice,
    baseListPrice: current.baseListPrice,
    baseDiscountedPrice: current.baseDiscountedPrice,
    setupFeeListPrice: current.setupFeeListPrice,
    setupFeeDiscountedPrice: current.setupFeeDiscountedPrice,
    volumeDiscount: current.discountMetaData?.volumeDiscountPercentage,
  };
};
